/**
 * The bar at the bottom of a video meeting: microphone, camera, screen share,
 * the side panel toggles, and the way out. "Close for everyone" is the host's
 * red button and always asks first — it ends the call for people who did not
 * press anything.
 */
import {
  Captions,
  Disc,
  LayoutGrid,
  MessageSquare,
  Mic,
  MicOff,
  MonitorOff,
  MonitorUp,
  PhoneOff,
  UserSquare2,
  Users,
  Video,
  VideoOff,
  XOctagon,
} from 'lucide-react';
import type { MeetLayout } from './VideoGrid';
import { useI18n } from '../../contexts/I18nContext';
import { canShareScreen } from '../../contexts/VoiceRoomContext';
import { useConfirm } from '../ui/ConfirmProvider';

export type MeetPanel = 'people' | 'chat' | 'transcript' | null;

const BASE =
  'inline-flex h-11 min-w-11 items-center justify-center gap-1.5 rounded-full px-3 text-sm font-medium transition-colors disabled:opacity-50';

function Control({
  icon,
  label,
  active,
  danger,
  badge,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  /** Pressed look: mic off, camera off, panel open. */
  active?: boolean;
  danger?: boolean;
  badge?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={[
        BASE,
        'relative',
        danger
          ? 'bg-red-600 text-fixed-white hover:bg-red-700'
          : active
            ? 'bg-foreground text-background hover:bg-foreground/90'
            : 'bg-accent text-foreground hover:bg-muted',
      ].join(' ')}
    >
      {icon}
      {badge !== undefined && badge > 0 && (
        <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[11px] font-bold text-neutral-900">
          {badge}
        </span>
      )}
    </button>
  );
}

export function MeetControls({
  micEnabled,
  cameraEnabled,
  screenShareEnabled,
  panel,
  waitingCount,
  chatUnread,
  showTranscript,
  canEndForAll,
  canRecord,
  recording,
  onToggleRecording,
  layout,
  showLayoutToggle,
  onToggleLayout,
  onToggleMic,
  onToggleCamera,
  onToggleScreenShare,
  onTogglePanel,
  onLeave,
  onEndForAll,
}: {
  micEnabled: boolean;
  cameraEnabled: boolean;
  screenShareEnabled: boolean;
  panel: MeetPanel;
  waitingCount: number;
  /** Chat lines that arrived while the chat panel was closed. */
  chatUnread: number;
  showTranscript: boolean;
  canEndForAll: boolean;
  /** Colleagues (a Firmio account, not guests), and only when the browser can record. */
  canRecord: boolean;
  recording: boolean;
  onToggleRecording: () => void;
  layout: MeetLayout;
  /** Only with three or more people: with two, the layout is decided for you. */
  showLayoutToggle: boolean;
  onToggleLayout: () => void;
  onToggleMic: () => void;
  onToggleCamera: () => void;
  onToggleScreenShare: () => void;
  onTogglePanel: (panel: Exclude<MeetPanel, null>) => void;
  onLeave: () => void;
  onEndForAll: () => void;
}) {
  const { t } = useI18n();
  const confirm = useConfirm();

  const endForAll = async () => {
    const yes = await confirm({
      title: t('meet.controls.endForAll'),
      message: t('meet.controls.confirmEndForAll'),
      confirmLabel: t('meet.controls.endForAll'),
      danger: true,
    });
    if (yes) onEndForAll();
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 px-3 py-3">
      <Control
        icon={micEnabled ? <Mic size={18} /> : <MicOff size={18} />}
        label={micEnabled ? t('meet.controls.mute') : t('meet.controls.unmute')}
        active={!micEnabled}
        onClick={onToggleMic}
      />
      <Control
        icon={cameraEnabled ? <Video size={18} /> : <VideoOff size={18} />}
        label={cameraEnabled ? t('meet.controls.cameraOff') : t('meet.controls.cameraOn')}
        active={!cameraEnabled}
        onClick={onToggleCamera}
      />
      {canShareScreen() && (
        <Control
          icon={screenShareEnabled ? <MonitorOff size={18} /> : <MonitorUp size={18} />}
          label={screenShareEnabled ? t('meet.controls.stopSharing') : t('meet.controls.share')}
          active={screenShareEnabled}
          onClick={onToggleScreenShare}
        />
      )}
      {canRecord && (
        <Control
          icon={<Disc size={18} className={recording ? 'animate-pulse text-red-500' : undefined} />}
          label={recording ? t('meet.recording.stop') : t('meet.recording.start')}
          active={recording}
          onClick={onToggleRecording}
        />
      )}
      {showLayoutToggle && (
        <Control
          icon={layout === 'grid' ? <UserSquare2 size={18} /> : <LayoutGrid size={18} />}
          label={layout === 'grid' ? t('meet.layout.speaker') : t('meet.layout.grid')}
          onClick={onToggleLayout}
        />
      )}
      <Control
        icon={<Users size={18} />}
        label={t('meet.controls.people')}
        active={panel === 'people'}
        badge={waitingCount}
        onClick={() => onTogglePanel('people')}
      />
      <Control
        icon={<MessageSquare size={18} />}
        label={t('meet.controls.chat')}
        active={panel === 'chat'}
        badge={chatUnread}
        onClick={() => onTogglePanel('chat')}
      />
      {showTranscript && (
        <Control
          icon={<Captions size={18} />}
          label={t('meet.controls.transcript')}
          active={panel === 'transcript'}
          onClick={() => onTogglePanel('transcript')}
        />
      )}
      <span className="mx-1 hidden h-6 w-px bg-border sm:block" />
      <button
        type="button"
        onClick={onLeave}
        title={t('meet.controls.leave')}
        className={`${BASE} bg-red-600 text-fixed-white hover:bg-red-700`}
      >
        <PhoneOff size={18} />
        <span className="hidden sm:inline">{t('meet.controls.leave')}</span>
      </button>
      {canEndForAll && (
        <Control
          icon={<XOctagon size={18} />}
          label={t('meet.controls.endForAll')}
          danger
          onClick={() => void endForAll()}
        />
      )}
    </div>
  );
}
